import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Brain, ArrowLeft, Trash2, Search, Sparkles, FileText, Plus, X } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/toastContext';
import { useConfirm } from '../../components/ui/useConfirm';

interface KnowledgeItem {
  id: string;
  content: string;
  category: string;
  confidence: number;
  created_at: string;
}

interface SearchResult {
  vector_id: string;
  knowledge_item_id: string | null;
  document_id: string | null;
  chunk_index: number;
  content: string;
  score: number;
  metadata: Record<string, unknown>;
}

export default function KnowledgeBase() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'semantic' | 'hybrid'>('semantic');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');

  const loadKnowledge = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/agents/${agentId}/knowledge`);
      setItems(res.data);
    } catch (e) {
      console.error('Failed to load knowledge:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadKnowledge();
    };
    tick();
  }, [loadKnowledge]);

  const deleteItem = async (id: string) => {
    if (!(await confirm({ title: '确定删除这条知识？', danger: true }))) return;
    try {
      await apiClient.delete(`/agents/${agentId}/knowledge/${id}`);
      setItems(items.filter(i => i.id !== id));
    } catch (e) {
      console.error('Failed to delete knowledge:', e);
      toast.error('删除失败，请稍后重试');
    }
  };

  const performSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await apiClient.post(`/agents/${agentId}/knowledge/search`, null, {
        params: { query: query.trim(), mode: searchMode, top_k: 10 },
      });
      setSearchResults(res.data.results || []);
    } catch (e) {
      console.error('Search failed:', e);
      toast.error('搜索失败，请检查 Embedding 服务配置');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const createKnowledge = async () => {
    if (!newContent.trim() || !agentId) return;
    setCreating(true);
    try {
      await apiClient.post(`/agents/${agentId}/knowledge`, {
        content: newContent.trim(),
        category: newCategory.trim() || undefined,
        source_type: 'manual',
        confidence: 1.0,
      });
      toast.success('知识添加成功');
      setNewContent('');
      setNewCategory('');
      setShowCreate(false);
      await loadKnowledge();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || (typeof err.message === 'string' && err.message) || '未知错误';
      toast.error('添加失败: ' + msg);
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading"><div className="spinner"></div></div>
      </div>
    );
  }

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="card mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2><Brain size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />知识库</h2>
            <p>管理 Agent 已学会的知识条目，支持语义检索</p>
          </div>
          <span className="badge info">{items.length} 条知识</span>
          <button className="primary text-sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} className="inline mr-1" /> 添加知识
          </button>
        </div>
      </div>

      {/* ── Create Dialog ── */}
      {showCreate && (
        <div className="card mb-4" style={{ background: 'rgba(126,231,135,0.12)', borderColor: 'var(--color-success)' }}>
          <div className="flex items-center justify-between mb-3">
            <h4 style={{ margin: 0 }}>添加新知识</h4>
            <button className="secondary" style={{ padding: '4px 8px' }} onClick={() => setShowCreate(false)}>
              <X size={14} />
            </button>
          </div>
          <div className="flex flex-col gap-3">
            <textarea
              rows={4}
              placeholder="输入知识内容..."
              value={newContent}
              onChange={e => setNewContent(e.target.value)}
            />
            <input
              placeholder="分类（可选，如：产品规范、FAQ、流程）"
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
            />
            <div className="flex gap-2 justify-end">
              <button className="secondary" onClick={() => setShowCreate(false)}>取消</button>
              <button
                className="primary"
                onClick={createKnowledge}
                disabled={creating || !newContent.trim()}
              >
                {creating ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Search Box ── */}
      <div className="card mb-4" style={{ background: 'var(--bg-tertiary)' }}>
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && performSearch()}
              placeholder="输入问题，语义搜索知识库..."
              style={{ flex: 1 }}
            />
            <select
              value={searchMode}
              onChange={e => setSearchMode(e.target.value as 'semantic' | 'hybrid')}
              style={{ width: 120 }}
            >
              <option value="semantic">语义</option>
              <option value="hybrid">混合</option>
            </select>
            <button className="primary" onClick={performSearch} disabled={searching || !query.trim()}>
              <Search size={14} /> {searching ? '搜索中...' : '搜索'}
            </button>
            {hasSearched && (
              <button className="secondary" onClick={clearSearch}>
                清除
              </button>
            )}
          </div>
          <p className="text-muted" style={{ fontSize: 12, margin: 0 }}>
            语义搜索：理解问题含义，返回最相关的知识片段 &nbsp;|&nbsp;
            混合搜索：结合向量相似度与关键词匹配
          </p>
        </div>
      </div>

      {/* ── Search Results ── */}
      {hasSearched && (
        <div className="mb-4">
          <h3><Sparkles size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            搜索结果 ({searchResults.length})
          </h3>
          {searchResults.length === 0 ? (
            <div className="card">
              <p className="text-muted">未找到相关知识，尝试换一个问题</p>
            </div>
          ) : (
            <div className="list">
              {searchResults.map((r, i) => (
                <div key={r.vector_id} className="list-item" style={{ borderLeft: '4px solid var(--color-primary)' }}>
                  <div className="list-item-content">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="badge info">#{i + 1}</span>
                      <span className="badge success">相关度 {(r.score * 100).toFixed(1)}%</span>
                      {r.metadata && typeof r.metadata === 'object' && 'source' in r.metadata && (
                        <span className="badge warning"><FileText size={10} /> {String((r.metadata as Record<string, unknown>).source)}</span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: 14 }}>{r.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── All Knowledge Items ── */}
      {!hasSearched && (
        <>
          {items.length === 0 ? (
            <div className="card">
              <div className="empty">
                <div className="empty-icon"><Brain size={48} /></div>
                <h3>暂无知识</h3>
                <p>通过对话示教、文档示教等方式添加知识</p>
              </div>
            </div>
          ) : (
            <div className="list">
              {items.map(item => (
                <div key={item.id} className="list-item">
                  <div className="list-item-content">
                    <p style={{ margin: 0 }}>{item.content}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="badge info">{item.category || '未分类'}</span>
                      <small className="text-muted">置信度: {Math.round((item.confidence || 0) * 100)}%</small>
                    </div>
                  </div>
                  <button
                    className="secondary"
                    onClick={() => deleteItem(item.id)}
                    style={{ padding: '6px 12px' }}
                  >
                    <Trash2 size={14} /> 删除
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {dialog}
    </div>
  );
}
